import { useCallback } from '@wordpress/element';
import { api, invalidateCacheByPattern } from '../../helpers/api';

interface PricingCalculationOptions {
	formId: number;
	serviceId: number;
	bookingFieldId: number;
}

interface PricingRequest {
	promise: Promise<number | null>;
	controller: AbortController;
	params: string; // JSON stringified params for comparison
}

// Module-level map to track in-flight requests per form+field
// This prevents race conditions when parameters change rapidly (e.g., occupancy updates)
const requestMap = new Map<string, PricingRequest>();

export function usePricingCalculation({
	formId,
	serviceId,
	bookingFieldId,
}: PricingCalculationOptions) {
	/**
	 * Resolves to null when the request was superseded or failed, meaning
	 * "leave the current price alone". That is not the same as a price of zero.
	 */
	const calculatePrice = useCallback(
		async (data: {
			resource_ids?: number[];
			start_time?: string;
			end_time?: string;
			quantity?: number;
			occupancy?: number;
			field_id: number;
		}): Promise<number | null> => {
			// Don't send request if we don't have a start time as it will 400
			if (!data.start_time) {
				return 0;
			}

			const pricingData = {
				service_id: serviceId,
				form_id: formId,
				...data,
			};

			// Create unique key for this form+field combination
			const requestKey = `${formId}:${data.field_id}`;
			const paramsKey = JSON.stringify(pricingData);

			// Check if there's an in-flight request for this form+field
			const existing = requestMap.get(requestKey);
			if (existing) {
				// If params are identical, return the existing promise
				if (existing.params === paramsKey) {
					return existing.promise;
				}
				// Params changed, abort the old request
				existing.controller.abort();
				requestMap.delete(requestKey);
			}

			// Create new AbortController for this request
			const controller = new AbortController();

			// Create the promise for this request
			const promise = api
				.post('/gp-bookings/v1/calculate-price', {
					data: pricingData,
					options: {
						signal: controller.signal,
						headers: {
							'X-Enable-Fetch-Cache': 'true',
						},
					},
				})
				.then((result) => {
					// A superseded request must not delete or overwrite its replacement.
					if (requestMap.get(requestKey)?.controller !== controller) {
						return null;
					}

					requestMap.delete(requestKey);
					return result.price || 0;
				})
				.catch((error) => {
					const isCurrentRequest =
						requestMap.get(requestKey)?.controller === controller;

					if (isCurrentRequest) {
						requestMap.delete(requestKey);
					}

					// Ignore abort errors (expected when we cancel requests)
					if (error.name === 'AbortError' || !isCurrentRequest) {
						return null;
					}

					// eslint-disable-next-line no-console
					console.error(
						'GP Bookings: Price calculation error:',
						error
					);

					return null;
				});

			// Store the controller so only this request's completion may remove it.
			requestMap.set(requestKey, {
				promise,
				controller,
				params: paramsKey,
			});

			return promise;
		},
		[formId, serviceId]
	);

	const clearCache = useCallback(() => {
		// Abort only THIS field's in-flight request. A form can hold several
		// Booking fields, each recalculating on its own schedule, and aborting a
		// sibling's request loses that booking's price outright: the abort
		// resolves to null, so its caller returns without writing a price or
		// refreshing the total, and nothing retries it. The sibling is then
		// stuck at whatever its price input already said - $0.00 on a first
		// render - and the Total is short by that booking for good.
		const requestKey = `${formId}:${bookingFieldId}`;
		const inFlight = requestMap.get(requestKey);

		if (inFlight) {
			inFlight.controller.abort();
			requestMap.delete(requestKey);
		}

		// Clear all pricing calculation cache entries. These are keyed by URL,
		// which every booking field shares, so this cannot be narrowed to one
		// field - but it only costs the others a refetch.
		invalidateCacheByPattern('/gp-bookings/v1/calculate-price');
	}, [formId, bookingFieldId]);

	return {
		calculatePrice,
		clearCache,
	};
}
